import Button from 'components/buttons/Button';
import ModalSkeleton from 'components/global/Skeleton/ModalSkeleton/ModalSkeleton';
import { Modal } from 'components/Modal/Modal';
import { ModalSchedulerTabs } from 'configs/types';
import { usePermissionHelper } from 'helpers/usePermissionHelper';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store';
import { closeModal } from 'store/modals/slice';
import {
  createSchedulerItem,
  getSchedulerItem,
  handleClearFields,
  handleUpdateFieldDraftItem,
  IScheduler,
  SchedulerItem,
  updateSchedulerItem
} from 'store/scheduler/slice';
import cn from 'clsx';
import styles from './ScheduleModal.module.scss';
import InnerInput from 'components/inputs/InnerInput/InnerInput';
import DefaultDropdown from 'components/dropdowns/DefaultDropdown/DefaultDropdown';
import { SchedulerTypeTabs } from './SchedulerTypeTabs';
import iconCalendar from '../../assets/icons/calendar.svg';
import { useDays } from './useDays';
import SwitchBtn from 'components/buttons/SwitchBtn';
import { NotificationTypes, showNotification } from 'store/notifications/slice';
import { schedulerTaskType } from 'store/scheduler/serializeSchedulerItem';

type Props = {
};

export const SchedulerModal: React.FC<Props> = () => {
  const { t } = useTranslation();
  
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [tabs] = useState<ModalSchedulerTabs[]>([
    ModalSchedulerTabs.main,
  ]);

  const entityId = searchParams.get("entityId");
  const isItemFetched = useAppSelector((s) => s.scheduler.isItemFetched);
  const isItemFetching = useAppSelector((s) => s.scheduler.isItemFetching);
  const [activeTab, setActiveTab] = useState<ModalSchedulerTabs>(ModalSchedulerTabs.main);
  const permissions = usePermissionHelper();
  const canSave = entityId
    ? Boolean(permissions?.update)
    : Boolean(permissions?.create);
  const services = useAppSelector((s) => s.settings.services).map(v => ({
    value: v,
    label: v
  }));
  const payload = useAppSelector((store) => store["scheduler"].draftItem);

  const isLoaded = () => {
  if (searchParams.get("entityId") !== null && !isItemFetched) {
    return false;
  }

  return true;
};

  const handleCloseClick = () => {
    navigate(location.pathname);
    dispatch(closeModal());
    dispatch(handleClearFields());
  };

  const handleChangeSchedule = (data: Partial<SchedulerItem>) => {
    dispatch(handleUpdateFieldDraftItem({
      ...payload,
      schedule: data as IScheduler ?? {} as IScheduler
    }))
  };

  const handleChangeScheduleTab = (data: IScheduler['type']) => {

    if (payload?.schedule?.type === data) return;
  
    const days = payload?.schedule?.days || [];

    dispatch(handleUpdateFieldDraftItem({
      ...payload,
      schedule:
        data === 'interval'
          ? { type: 'interval', interval: 1, days }
          : { type: 'specific', specific: [], days }
    }));
  };

  const handleChangeTitle = (data: string) => {
    dispatch(handleUpdateFieldDraftItem({ title: data }))
  };

  
  const handleChangeService= (data: string) => {
    dispatch(handleUpdateFieldDraftItem({ service: data }))
  };

  const handleChangeType = (data: string) => {
    const normalizeType = (s: string) => s.toLowerCase().replace(/\s+/g, "_");
    dispatch(handleUpdateFieldDraftItem({ type: normalizeType(data) }))
  };

  const handleClose = () => {
    navigate(`${location.pathname}`);
    dispatch(closeModal());
    dispatch(handleClearFields());
  };

  const handleApply = () => {
    if (!canSave) {
      return;
    }

    if (!entityId) {
      //@ts-ignore
      dispatch(createSchedulerItem(payload))
        .unwrap()
        .then(() => {
          dispatch(
            showNotification({
              title: t("success"),
              message:
                searchParams.get("entityId") !== null
                  ? t("entityUpdateSuccess")
                  : t("entityCreateSuccess"),
              type: NotificationTypes.success,
            }),
          );
          handleClose();
        })
        .catch((e) => {
          dispatch(
            showNotification({
              title: t("error"),
              message:
                searchParams.get("entityId") !== null
                  ? t("entityUpdateError")
                  : t("entityCreateError"),
              type: NotificationTypes.error,
            }),
          );
        });
      
      return;
    }

    //@ts-ignore
    dispatch(updateSchedulerItem(payload))
      .unwrap()
      .then(() => {
        dispatch(
          showNotification({
            title: t("success"),
            message:
              searchParams.get("entityId") !== null
                ? t("entityUpdateSuccess")
                : t("entityCreateSuccess"),
            type: NotificationTypes.success,
          }),
        );
        handleClose();
      })
      .catch(() => {
        dispatch(
          showNotification({
            title: t("error"),
            message:
              searchParams.get("entityId") !== null
                ? t("entityUpdateError")
                : t("entityCreateError"),
            type: NotificationTypes.error,
          }),
        );
      });
  }

  const days = useDays({ days: payload.schedule?.days || [] });

  useEffect(() => {
    if (entityId && !isItemFetched) {
      dispatch(getSchedulerItem(+entityId));
    }
  }, [entityId, isItemFetched])

  return (
    <Modal onClose={handleCloseClick}>
      <Modal.Header>{t("scheduler")}</Modal.Header>
      {!isLoaded() ? (
        <ModalSkeleton />
      ) : (
        <>
          <Modal.Tabs
            tabs={tabs}
            activeTab={activeTab}
          />

          <Modal.Content className={cn(styles.schedulerContent)}>
              <div className={cn(styles.schedulerColumn)}>
                  <InnerInput
                    value={payload?.id ? `${payload?.id}` : '1'}
                    placeholder={t("id")}
                    label={t("id")}
                    type="text"
                    id="id"
                    disabled
                    onChange={(e) => {}}
                  />
                  <InnerInput
                    value={payload?.title ?? ''}
                    placeholder={t("title")}
                    label={t("title")}
                    type="text"
                    id="title"
                    disabled={!canSave}
                    onChange={handleChangeTitle}
                   />
                  <DefaultDropdown
                    value={payload?.service}
                    label={t("service")}
                    placeholder={t("service")}
                    options={services}
                    disabled={!canSave}
                    onSelect={(e) => handleChangeService(e.toString())}
                />
                  <InnerInput
                    value={payload?.type || ''}
                    placeholder={t("type")}
                    label={t("type")}
                    type="text"
                    id="type"
                    onChange={handleChangeType}
                    disabled={!canSave}
                   />
                <div>
                  <SchedulerTypeTabs
                    activeTab={payload?.schedule?.type || 'interval'}
                    onChangeActiveTab={handleChangeScheduleTab}
                    scheduleDraft={payload?.schedule}
                    onChangeSchedule={handleChangeSchedule}
                    readOnly={!canSave}
                  />
                </div>
              </div>
              <div className={cn(styles.schedulerColumn, styles.schedulerColumnPreview)}>
                <div className={cn(styles.schedulerColumnPreviewHeader)}>
                  <div className={cn(styles.schedulerColumnPreviewIconHeader)}>
                    <img src={iconCalendar} alt="Icon Calendar" />
                  </div>
                  <h3>{t("schedulePreview")}</h3>
                </div>
                <div className={cn(styles.schedulerColumnPreviewContent)}>
                  <div className={cn(styles.schedulerColumnPreviewRow)}>
                    <h6>{t("title")}</h6>
                    <p>{payload?.title}</p>
                  </div>
                  <div className={cn(styles.schedulerColumnPreviewRow)}>
                    <h6>{t("service")}</h6>
                    <p>{payload?.service}</p>
                  </div>
                  <div className={cn(styles.schedulerColumnPreviewRow)}>
                    <h6>{t("type")}</h6>
                    <p>{schedulerTaskType(payload)}</p>
                  </div>
                </div>
                <div className={cn(styles.schedulerColumnPreviewDivider)} />
                <div className={cn(styles.schedulerColumnPreviewContent)}>
                  <div className={cn(styles.schedulerColumnPreviewRow, "row")}>
                    <h6>{t("scheduleType")}</h6>
                      <div className="badge badge--success">
                        {payload?.schedule?.type}
                      </div>
                  </div>
                  {payload.schedule?.type === 'interval' && (
                    <>
                      <div className={cn(styles.schedulerColumnPreviewRow, "row")}>
                        <h6>{t("runsEvery")}</h6>
                        <p>{payload?.schedule.interval} minute</p>
                      </div>
                      <div className={cn(styles.schedulerColumnPreviewRow, "row")}>
                        <h6>{t("activeOn")}</h6>
                        <div>{days.join(', ')}</div>
                      </div>
                    </>
                  )}
                  {payload.schedule?.type === 'specific' && (
                    <>
                      <div className={cn(styles.schedulerColumnPreviewRow, "row")}>
                        <h6>{t("runsAt")}</h6>
                        <p>{payload?.schedule.specific.join(" and ")}</p>
                      </div>
                      <div className={cn(styles.schedulerColumnPreviewRow, "row")}>
                        <h6>{t("activeOn")}</h6>
                        <div>{days.join(', ')}</div>
                      </div>
                    </>
                  )}
                  <div className={cn(styles.schedulerColumnPreviewDivider)} />
                  <div className={cn(styles.schedulerColumnPreviewRow, "row")}>
                    <h6>{t("status")}</h6>
                    <SwitchBtn
                      activeTxt={t("active")}
                      notActiveTxt={t("hidden")}
                      isActive={!!payload.isActive}
                      disabled={!canSave}
                      handleClick={() =>
                        dispatch(handleUpdateFieldDraftItem({ isActive: payload.isActive ? 0 : 1 }))
                      }
                    />

                  </div>
                </div>
              </div>
          </Modal.Content>

          {canSave && (
              <Modal.Footer>
                <Button
                  isSecondary
                  txt={t("cancel")}
                  handleClick={handleCloseClick}
                />

                <Button
                  isPrimary
                  txt={t("save")}
                  handleClick={handleApply}
                  isLoad={isItemFetching}
                />
              </Modal.Footer>
          )}
        </>
      )}
    </Modal>
  );
};
